import React, { useCallback, useRef, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Box, Typography } from '@mui/material';
import DTWidgetCard from './DTWidgetCard';
import { getDefaultWidgetH } from './registry/dtWidgetTypes';

const ResponsiveGridLayout = WidthProvider(Responsive);
const MAX_COLS = 4;
const ROW_HEIGHT = 80;
const MARGIN = 8;

// ─── Layout helpers ──────────────────────────────────────────────────────────

function distributeColumns(count) {
  const base = Math.floor(MAX_COLS / count);
  const extra = MAX_COLS % count;
  const slots = [];
  let cursor = 0;
  for (let i = 0; i < count; i++) {
    const w = base + (i < extra ? 1 : 0);
    slots.push({ x: cursor, w });
    cursor += w;
  }
  return slots;
}

export function addWidgetWithLayout(existingWidgets, newWidget) {
  const defaultH = newWidget.gridmeta?.h || getDefaultWidgetH(newWidget.type);

  if (existingWidgets.length === 0) {
    return [{ ...newWidget, gridmeta: { x: 0, y: 0, w: MAX_COLS, h: defaultH } }];
  }

  const maxY = Math.max(...existingWidgets.map((w) => w.gridmeta?.y ?? 0));
  const lastRow = existingWidgets.filter((w) => (w.gridmeta?.y ?? 0) === maxY);
  const others  = existingWidgets.filter((w) => (w.gridmeta?.y ?? 0) !== maxY);

  if (lastRow.length < MAX_COLS) {
    const newCount = lastRow.length + 1;
    const slots = distributeColumns(newCount);
    const updatedRow = lastRow.map((w, i) => ({
      ...w, gridmeta: { ...w.gridmeta, x: slots[i].x, w: slots[i].w },
    }));
    return [
      ...others,
      ...updatedRow,
      { ...newWidget, gridmeta: { x: slots[newCount - 1].x, y: maxY, w: slots[newCount - 1].w, h: defaultH } },
    ];
  }

  const newY = Math.max(
    ...existingWidgets.map((w) => (w.gridmeta?.y ?? 0) + (w.gridmeta?.h ?? getDefaultWidgetH(w.type)))
  );
  return [...existingWidgets, { ...newWidget, gridmeta: { x: 0, y: newY, w: MAX_COLS, h: defaultH } }];
}

/** @deprecated — kept for legacy callers */
export function packNewWidget(widgets) {
  const dummy = { id: '__dummy__', type: 'unknown', gridmeta: { h: 5 } };
  const result = addWidgetWithLayout(widgets, dummy);
  return result[result.length - 1].gridmeta;
}

// ─── Custom resize wrapper ────────────────────────────────────────────────────

/**
 * Adds invisible resize handles (right, bottom, bottom-right) to its child.
 * Cursor changes to the matching resize cursor on hover.
 * On drag-release the new grid dimensions are reported via onResize(id, newW, newH).
 */
function ResizableWrapper({ widget, isEditMode, onResize, children }) {
  const wrapperRef = useRef(null);

  const startResize = useCallback(
    (direction, e) => {
      if (!isEditMode) return;
      e.stopPropagation();
      e.preventDefault();

      const el = wrapperRef.current;
      if (!el) return;

      const startX  = e.clientX;
      const startY  = e.clientY;
      const startPxW = el.offsetWidth;
      const startPxH = el.offsetHeight;

      // Column width = container width ÷ MAX_COLS
      const container  = el.closest('.layout');
      const containerW = container?.offsetWidth ?? el.parentElement?.offsetWidth ?? 800;
      const colW = containerW / MAX_COLS;

      function calcDims(clientX, clientY) {
        const dx = clientX - startX;
        const dy = clientY - startY;
        let newW = widget.gridmeta.w;
        let newH = widget.gridmeta.h;
        if (direction === 'e' || direction === 'se') {
          newW = Math.max(1, Math.min(MAX_COLS, Math.round((startPxW + dx) / colW)));
        }
        if (direction === 's' || direction === 'se') {
          newH = Math.max(3, Math.round((startPxH + dy) / (ROW_HEIGHT + MARGIN)));
        }
        return { newW, newH };
      }

      const onMouseUp = (upEvt) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        const { newW, newH } = calcDims(upEvt.clientX, upEvt.clientY);
        onResize(widget.id, newW, newH);
      };

      const onMouseMove = (moveEvt) => {
        calcDims(moveEvt.clientX, moveEvt.clientY); // keep for potential live preview
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      // Lock cursor for the whole drag
      const cur = direction === 'e' ? 'ew-resize' : direction === 's' ? 'ns-resize' : 'nwse-resize';
      document.body.style.cursor = cur;
    },
    [isEditMode, widget, onResize]
  );

  const handleSx = {
    position: 'absolute',
    zIndex: 50,
    userSelect: 'none',
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {children}

      {isEditMode && (
        <>
          {/* Right edge */}
          <Box
            className="resize-handle"
            sx={{ ...handleSx, top: 8, bottom: 8, right: 0, width: 8, cursor: 'ew-resize' }}
            onMouseDown={(e) => startResize('e', e)}
          />
          {/* Bottom edge */}
          <Box
            className="resize-handle"
            sx={{ ...handleSx, left: 8, right: 8, bottom: 0, height: 8, cursor: 'ns-resize' }}
            onMouseDown={(e) => startResize('s', e)}
          />
          {/* Bottom-right corner */}
          <Box
            className="resize-handle"
            sx={{ ...handleSx, right: 0, bottom: 0, width: 16, height: 16, cursor: 'nwse-resize' }}
            onMouseDown={(e) => startResize('se', e)}
          />
        </>
      )}
    </div>
  );
}

// ─── Canvas ───────────────────────────────────────────────────────────────────

function DTDashboardCanvas({
  widgets,
  isEditMode,
  theme,
  componentData,
  onWidgetEdit,
  onWidgetDelete,
  onWidgetClone,
  onLayoutChange,
}) {
  const isDark = theme === 'dark';

  // Stable ref so drag/resize callbacks don't change when widgets array changes
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;

  const layouts = useMemo(() => ({
    lg: widgets.map((w) => {
      const minH = getDefaultWidgetH(w.type);
      return {
        i: w.id,
        x: w.gridmeta?.x ?? 0,
        y: w.gridmeta?.y ?? 0,
        w: w.gridmeta?.w ?? 2,
        h: w.gridmeta?.h ?? minH,
        minW: 1,
        minH,
      };
    }),
  }), [widgets]);

  // Drag stop — save new x/y positions
  const handleDragStop = useCallback(
    (layout) => {
      if (!isEditMode) return;
      const updated = widgetsRef.current.map((w) => {
        const item = layout.find((l) => l.i === w.id);
        return item ? { ...w, gridmeta: { ...w.gridmeta, x: item.x, y: item.y } } : w;
      });
      onLayoutChange(updated);
    },
    [isEditMode, onLayoutChange]
  );

  // Custom resize stop — save new w/h
  const handleWidgetResize = useCallback(
    (widgetId, newW, newH) => {
      const updated = widgetsRef.current.map((w) =>
        w.id === widgetId
          ? { ...w, gridmeta: { ...w.gridmeta, w: newW, h: newH } }
          : w
      );
      onLayoutChange(updated);
    },
    [onLayoutChange]
  );

  if (!widgets.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.4 }}>
        <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#000' }}>
          {isEditMode ? 'Click a widget from the library to add it.' : 'No widgets in this dashboard.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: 'visible' }}>
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 0 }}
        cols={{ lg: MAX_COLS }}
        rowHeight={ROW_HEIGHT}
        isDraggable={isEditMode}
        isResizable={false}
        draggableCancel=".resize-handle,.widget-action-buttons"
        onDragStop={handleDragStop}
        margin={[MARGIN, MARGIN]}
        containerPadding={[2, 2]}
      >
        {widgets.map((widget) => (
          <div key={widget.id} style={{ height: '100%', width: '100%' }}>
            <ResizableWrapper
              widget={widget}
              isEditMode={isEditMode}
              onResize={handleWidgetResize}
            >
              <DTWidgetCard
                widget={widget}
                isEditMode={isEditMode}
                theme={theme}
                componentData={componentData}
                onEdit={onWidgetEdit}
                onDelete={onWidgetDelete}
                onClone={onWidgetClone}
              />
            </ResizableWrapper>
          </div>
        ))}
      </ResponsiveGridLayout>
    </Box>
  );
}

export default React.memo(DTDashboardCanvas);
